/*
 *
 * AdminPage / Users / UserDetail
 *
 * Detail for a single user. Backed by GET /api/v3/admin/users/:id, which
 * returns the ADMIN_GET_USER_SHAPE object (id, customer_name, email,
 * expiration_date, first_name, last_name, permission_role [enum STRING], plus
 * subscription/trial fields). permission_role is rendered via roleLabel.
 *
 * The superadmin mutations (edit role, assign unlimited plan, delete) live in
 * the UserActions section below the read-only fields.
 */

import React from "react";
import { useParams, useNavigate } from "react-router-dom";

import Loader from "components/Loader";
import { useAdminUser } from "../api/queries";
import { roleLabel } from "./roles";
import UserActions from "./UserActions";
import {
  Wrapper,
  BackLink,
  DetailHeading,
  FieldGrid,
  Field,
  DetailLabel,
  FieldValue,
} from "./styledComponents";

// Render order for the detail fields, paired with a formatter. Keeping this as
// data (not inline JSX) makes the field set easy to audit against the API shape.
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const yesNo = (v) => (v === null || v === undefined ? "—" : v ? "Yes" : "No");

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: user, isLoading, isError } = useAdminUser(id);

  if (isLoading) {
    return (
      <Wrapper>
        <div data-testid="user-detail-loading">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  if (isError || !user) {
    return (
      <Wrapper>
        <BackLink type="button" onClick={() => navigate("/admin/users")}>
          ← Back to users
        </BackLink>
        <p className="admin-users__error">Couldn’t load this user.</p>
      </Wrapper>
    );
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");

  return (
    <Wrapper>
      <BackLink type="button" onClick={() => navigate("/admin/users")}>
        ← Back to users
      </BackLink>
      <DetailHeading>
        <h1>{fullName || user.email}</h1>
        <span className="admin-users__role">{roleLabel(user.permission_role)}</span>
      </DetailHeading>

      <FieldGrid>
        <Field>
          <DetailLabel>Email</DetailLabel>
          <FieldValue>{fmt(user.email)}</FieldValue>
        </Field>
        <Field>
          <DetailLabel>Customer</DetailLabel>
          <FieldValue>{fmt(user.customer_name)}</FieldValue>
        </Field>
        <Field>
          <DetailLabel>Role</DetailLabel>
          <FieldValue>{roleLabel(user.permission_role)}</FieldValue>
        </Field>
        <Field>
          <DetailLabel>Expiration date</DetailLabel>
          <FieldValue>{fmt(user.expiration_date)}</FieldValue>
        </Field>
        <Field>
          <DetailLabel>Renewal date</DetailLabel>
          <FieldValue>{fmt(user.renewal_date)}</FieldValue>
        </Field>
        <Field>
          <DetailLabel>Days left in renewal</DetailLabel>
          <FieldValue>{fmt(user.remaining_days_in_renewel)}</FieldValue>
        </Field>
        <Field>
          <DetailLabel>Trial channel remaining</DetailLabel>
          <FieldValue>{yesNo(user.remaining_trial_channel)}</FieldValue>
        </Field>
        <Field>
          <DetailLabel>Lite channel remaining</DetailLabel>
          <FieldValue>{yesNo(user.remaining_lite_channel)}</FieldValue>
        </Field>
      </FieldGrid>

      <UserActions user={user} />
    </Wrapper>
  );
};

export default UserDetail;
